import { useNavigate as useRouterNavigate, NavigateOptions } from 'react-router-dom';
import { ApiError } from 'services/api.errors';
import {
  ForgotPasswordPayload,
  ID,
  IndependentIraUser,
  IraUser,
  UserEmailCheckResponse,
} from 'services/requests';
import { useRole } from 'hooks/common/useRole';
import {
  ROUTE_ETZ_PROFILE_TRANSACTIONS,
  ROUTE_ETZ_PROFILE_ASSETS,
  ROUTE_ETZ_PROFILE_REVENUE,
  ROUTE_ETZ_PROFILE_INFLOW_OUTFLOW,
  ROUTE_EMPLOYERS,
  ROUTE_LOGIN,
  ROUTE_LOGIN_BLOCKED,
  ROUTE_LOGIN_VERIFY,
  ROUTE_FORGOT_PASSWORD,
  ROUTE_FORGOT_PASSWORD_VERIFY,
  ROUTE_SET_NEW_PASSWORD,
  ROUTE_IRA_ACCOUNTS,
  ROUTE_IRA_ACCOUNTS_NEW,
  ROUTE_CONTRIBUTIONS,
} from 'constants/routes';

const ERROR_MESSAGE = 'Navigate failed, employerId is undefined';

type RouteParameter = {
  employerId: string | undefined;
  iraUserId: string | undefined;
};

type NewUserWithState = Pick<RouteParameter, 'employerId'> & {
  state: UserEmailCheckResponse & Partial<Pick<IraUser, 'custodianType'>>;
} & Partial<Pick<IraUser, 'etzFee'>> &
  Partial<Pick<IraUser, 'profileType'>>;

export type ContributionDetailsState = {
  transferId: string;
  status: string;
};

export type NewIndependentIraState = UserEmailCheckResponse &
  Partial<Pick<IndependentIraUser, 'annualFee'>> &
  Partial<Pick<IndependentIraUser, 'etzFee'>> &
  Partial<Pick<IndependentIraUser, 'custodianType'>>;

// TODO: Refactor it to remove hardcode routes

export function useNavigate() {
  const navigate = useRouterNavigate();
  const { isAdmin } = useRole();

  const routes = {
    toLoginVerify(state: unknown) {
      navigate(ROUTE_LOGIN_VERIFY, { state });
    },

    toLogin() {
      navigate(ROUTE_LOGIN, { replace: true });
    },

    toLoginBlocked(state?: ApiError) {
      return navigate(ROUTE_LOGIN_BLOCKED, { replace: true, state: state });
    },

    toEtzProfileTransactions() {
      navigate(ROUTE_ETZ_PROFILE_TRANSACTIONS);
    },

    toEtzProfileAssets() {
      navigate(ROUTE_ETZ_PROFILE_ASSETS);
    },

    toEtzProfileRevenueByMonth() {
      navigate(ROUTE_ETZ_PROFILE_REVENUE);
    },

    toEtzProfileInflowOutflow() {
      navigate(ROUTE_ETZ_PROFILE_INFLOW_OUTFLOW);
    },

    toEmployers() {
      navigate(ROUTE_EMPLOYERS);
    },

    toIraUsersRole({ employerId }: Pick<RouteParameter, 'employerId'>) {
      return routes.toIraUsersRoleAdmin(employerId);
      // NOTE: Disabled in ETZ-2825. Need to check actuality. Related to 'Role User'
      // if (isAdmin) {
      //   return routes.toIraUsersRoleAdmin(employerId);
      // }
      // return routes.toIraUsersRoleUser();
    },

    // NOTE: Disabled in ETZ-2825. Need to check actuality. Related to 'Role User'
    // toIraUsersRoleUser() {
    //   navigate(ROUTE_IRA_USERS_USER);
    // },

    toIraUsersRoleAdmin(employerId: RouteParameter['employerId']) {
      if (!employerId) throw new Error(ERROR_MESSAGE);
      navigate(`/employers/${employerId}/employees`);
    },

    toNewIraUserRole({ employerId, state }: NewUserWithState) {
      return routes.toNewIraUserRoleAdmin(employerId, state);
      // NOTE: Disabled in ETZ-2825. Need to check actuality. Related to 'Role User'
      // if (isAdmin) {
      //   return routes.toNewIraUserRoleAdmin(employerId, state);
      // }
      // return routes.toNewIraUserRoleUser(state);
    },

    // NOTE: Disabled in ETZ-2825. Need to check actuality. Related to 'Role User'
    // toNewIraUserRoleUser(state: UserEmailCheckResponse) {
    //   navigate(ROUTE_NEW_IRAUSER_USER, { state });
    // },

    toNewIraUserRoleAdmin(
      employerId: RouteParameter['employerId'],
      state: UserEmailCheckResponse,
    ) {
      if (!employerId) throw new Error(ERROR_MESSAGE);
      navigate(`/employers/${employerId}/employees/new`, { state });
    },

    toIraUserRole({
      employerId,
      iraUserId,
    }: Pick<RouteParameter, 'employerId' | 'iraUserId'>) {
      if (isAdmin) {
        return routes.toIraUserRoleAdmin(employerId, iraUserId);
      }
      return routes.toIraUserRoleUser(iraUserId);
    },

    toIraUserRoleUser(iraUserId: string | undefined, options?: NavigateOptions) {
      if (!iraUserId) throw new Error(ERROR_MESSAGE);
      navigate(`/employees/${iraUserId}/general-info`, options);
    },

    toIraUserRoleAdmin(
      employerId: RouteParameter['employerId'],
      iraUserId: string | undefined,
      options?: NavigateOptions,
    ) {
      if (!employerId || !iraUserId) throw new Error(ERROR_MESSAGE);
      navigate(`/employers/${employerId}/employees/${iraUserId}/general-info`, options);
    },

    toIraUserTransactions(
      employerId: RouteParameter['employerId'],
      iraUserId: string | undefined,
      options?: NavigateOptions,
    ) {
      if (!employerId || !iraUserId) throw new Error(ERROR_MESSAGE);
      navigate(`/employers/${employerId}/employees/${iraUserId}/transactions`, options);
    },

    toIraUserOpenOrders(
      employerId: RouteParameter['employerId'],
      iraUserId: string | undefined,
      options?: NavigateOptions,
    ) {
      if (!employerId || !iraUserId) throw new Error(ERROR_MESSAGE);
      navigate(`/employers/${employerId}/employees/${iraUserId}/open-orders`, options);
    },

    toIraUserAssets(
      employerId: RouteParameter['employerId'],
      iraUserId: string | undefined,
      options?: NavigateOptions,
    ) {
      if (!employerId || !iraUserId) throw new Error(ERROR_MESSAGE);
      navigate(`/employers/${employerId}/employees/${iraUserId}/assets`, options);
    },

    toForgotPassword(state?: ForgotPasswordPayload) {
      navigate(ROUTE_FORGOT_PASSWORD, { state: state });
    },

    toForgotPasswordVerify(state?: ForgotPasswordPayload) {
      navigate(ROUTE_FORGOT_PASSWORD_VERIFY, { state: state });
    },

    toSetNewPassword(state?: ForgotPasswordPayload) {
      navigate(ROUTE_SET_NEW_PASSWORD, { state: state });
    },

    toIndependentIra() {
      navigate(ROUTE_IRA_ACCOUNTS);
    },
    toIndependentIraInfo(id?: ID, options?: NavigateOptions) {
      navigate(`/ira-accounts/${id}/general-info`, options);
    },
    toIndependentIraTransactions(id?: ID, options?: NavigateOptions) {
      navigate(`/ira-accounts/${id}/transactions`, options);
    },
    toIndependentIraOpenOrders(id?: ID, options?: NavigateOptions) {
      navigate(`/ira-accounts/${id}/open-orders`, options);
    },
    toIndependentIraAssets(id?: ID, options?: NavigateOptions) {
      navigate(`/ira-accounts/${id}/assets`, options);
    },
    toIndependentIraNew(state?: NewIndependentIraState) {
      navigate(ROUTE_IRA_ACCOUNTS_NEW, { state });
    },

    toIraUserDetailsThroughIndependentIraTable(userId?: ID, options?: NavigateOptions) {
      navigate(`/ira-accounts/ira-user/${userId}/general-info`, options);
    },
    toIraUserTransactionsThroughIndependentIraTable(
      userId?: ID,
      options?: NavigateOptions,
    ) {
      navigate(`/ira-accounts/ira-user/${userId}/transactions`, options);
    },
    toIraUserOpenOrdersThroughIndependentIraTable(
      userId?: ID,
      options?: NavigateOptions,
    ) {
      navigate(`/ira-accounts/ira-user/${userId}/open-orders`, options);
    },
    toIraUserAssetsThroughIndependentIraTable(userId?: ID, options?: NavigateOptions) {
      navigate(`/ira-accounts/ira-user/${userId}/assets`, options);
    },

    toContributions() {
      navigate(ROUTE_CONTRIBUTIONS);
    },
    toContributionDetails(id: ID, state?: ContributionDetailsState) {
      navigate(`/contributions/${id}/details`, { state });
    },
    toContributionIRAUserDetail(
      contributionId?: ID,
      userId?: ID,
      options?: NavigateOptions,
    ) {
      navigate(
        `/contributions/${contributionId}/details/ira-user/${userId}/general-info`,
        options,
      );
    },
    toContributionIRAUserDetailTransactions(
      contributionId?: ID,
      userId?: ID,
      options?: NavigateOptions,
    ) {
      navigate(
        `/contributions/${contributionId}/details/ira-user/${userId}/transactions`,
        options,
      );
    },
    toContributionIRAUserDetailOpenOrders(
      contributionId?: ID,
      userId?: ID,
      options?: NavigateOptions,
    ) {
      navigate(
        `/contributions/${contributionId}/details/ira-user/${userId}/open-orders`,
        options,
      );
    },
    toContributionIRAUserDetailAssets(
      contributionId?: ID,
      userId?: ID,
      options?: NavigateOptions,
    ) {
      navigate(
        `/contributions/${contributionId}/details/ira-user/${userId}/assets`,
        options,
      );
    },
    toContributionIndependentIRAAccountDetail(
      contributionId?: ID,
      userId?: ID,
      options?: NavigateOptions,
    ) {
      navigate(
        `/contributions/${contributionId}/details/ira-account/${userId}/general-info`,
        options,
      );
    },
    toContributionIndependentIRAAccountDetailTransactions(
      contributionId?: ID,
      userId?: ID,
      options?: NavigateOptions,
    ) {
      navigate(
        `/contributions/${contributionId}/details/ira-account/${userId}/transactions`,
        options,
      );
    },
    toContributionIndependentIRAAccountDetailOpenOrders(
      contributionId?: ID,
      userId?: ID,
      options?: NavigateOptions,
    ) {
      navigate(
        `/contributions/${contributionId}/details/ira-account/${userId}/open-orders`,
        options,
      );
    },
    toContributionIndependentIRAAccountDetailAssets(
      contributionId?: ID,
      userId?: ID,
      options?: NavigateOptions,
    ) {
      navigate(
        `/contributions/${contributionId}/details/ira-account/${userId}/assets`,
        options,
      );
    },

    toBack() {
      navigate(-1);
    },
  };

  return routes;
}
